import { useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'

import { PurchaseLotStockInForm } from './new'
import type { CreatePurchaseInNoteRequest } from '@/lib/api/purchase-lot-api'
import type { LotOption } from '@/components/lots/lot-items-field'
import { purchaseLotApi } from '@/lib/api/purchase-lot-api'
import {
    purchaseLotOption,
    purchaseNoteItemOption,
} from '@/components/lots/lot-option-builders'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import {
    usePurchaseInNote,
    usePurchaseLots,
    useUpdatePurchaseInNote,
} from '@/hooks/use-purchase-lot-query'
import { useTeaPurchasesList } from '@/hooks/use-tea-purchase-query'
import { useDebounced } from '@/hooks/use-debounced'
import { formatDateVN, formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/purchase-lots/stock-in-notes/$id_/edit',
)({
    component: Page,
})

function Page() {
    const { id } = Route.useParams()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { data, isLoading } = usePurchaseInNote(id)
    const note = data?.data
    const update = useUpdatePurchaseInNote()

    const [purchaseSearch, setPurchaseSearch] = useState('')
    const debouncedSearch = useDebounced(purchaseSearch, 300)
    const [purchaseLotSearch, setPurchaseLotSearch] = useState('')
    const debouncedPurchaseLotSearch = useDebounced(purchaseLotSearch, 300)

    // from_purchase combobox: chỉ đơn mua CHƯA có PNK (giống màn tạo) — đơn mua
    // hiện tại của phiếu được seed thêm ở dưới.
    const { data: freePurchasesData } = useTeaPurchasesList({
        processed: true,
        has_active_pnk: false,
        per_page: 50,
        search: debouncedSearch || undefined,
    })
    // Không lọc has_active_pnk — dùng làm category cho lô mới khi reason=adjustment.
    const { data: allPurchasesData } = useTeaPurchasesList({
        processed: true,
        per_page: 50,
        search: debouncedSearch || undefined,
    })
    const { data: lotsData } = usePurchaseLots({
        per_page: 200,
        ...(debouncedPurchaseLotSearch
            ? { search: debouncedPurchaseLotSearch }
            : {}),
    })

    const allTeaPurchaseOptions = useMemo(
        () =>
            (allPurchasesData?.data ?? []).map((purchase) => ({
                value: purchase.id,
                label: `${purchase.code} — ${purchase.grower_name ?? ''} — ${formatDateVN(purchase.purchase_date)} — ${formatQty(purchase.dry_quantity ?? purchase.quantity)} kg`,
            })),
        [allPurchasesData],
    )
    // Đơn mua chưa có PNK (nguồn hợp lệ để đổi sang cho phiếu from_purchase).
    const freeTeaPurchaseOptions = useMemo(
        () =>
            (freePurchasesData?.data ?? []).map((purchase) => ({
                value: purchase.id,
                label: `${purchase.code} — ${purchase.grower_name ?? ''} — ${formatDateVN(purchase.purchase_date)} — ${formatQty(purchase.dry_quantity ?? purchase.quantity)} kg`,
            })),
        [freePurchasesData],
    )
    const lotOptions = useMemo(
        () => (lotsData?.data ?? []).map(purchaseLotOption),
        [lotsData],
    )

    // Lô đã có trong phiếu có thể nằm ngoài top 200 → seed vào picker để hiện
    // đúng nhãn (giống cache lô quét mã).
    const noteLotSeeds = useMemo(
        () => (note?.items ?? []).map(purchaseNoteItemOption),
        [note],
    )

    const [scannedLots, setScannedLots] = useState<Map<string, LotOption>>(
        new Map(),
    )
    const fetchAndCacheLot = async (lotId: string) => {
        if (scannedLots.has(lotId)) return
        try {
            const res = await purchaseLotApi.getLot(lotId)
            const l = res.data
            if (!l) return
            setScannedLots((prev) => {
                if (prev.has(lotId)) return prev
                const next = new Map(prev)
                next.set(lotId, purchaseLotOption(l))
                return next
            })
        } catch {
            // Bỏ qua — row vẫn giữ đúng ID trong form state.
        }
    }

    if (isLoading) return <div className="p-6">{m.sl_detail_loading()}</div>
    if (!note) return <div className="p-6">{m.sl_detail_note_not_found()}</div>

    const isCreator = !!user?.id && note.created_by_id === user.id
    const editable =
        note.status === 'pending' && isCreator && !note.stocktake_session_id

    if (!editable) {
        return (
            <div className="space-y-4 p-6">
                <Button asChild variant="ghost" className="-ml-2">
                    <Link
                        to="/purchase-lots/stock-in-notes/$id"
                        params={{ id }}
                    >
                        <ArrowLeft className="size-4 mr-1" />
                        {m.sl_lot_back()}
                    </Link>
                </Button>
                <p className="text-muted-foreground">
                    {m.pl_pnk_edit_not_allowed()}
                </p>
            </div>
        )
    }

    // Điền sẵn form theo đúng màn tạo của loại phiếu:
    //   - from_purchase: ô "Đơn mua" (không phải danh sách lô) → lấy đơn mua
    //     nguồn từ item đầu (mọi lô cùng 1 đơn mua).
    //   - reason khác: danh sách lô, map thành row 'existing'.
    const isFromPurchase = note.reason === 'from_purchase'
    const firstItem = note.items?.[0]
    const initialValues = {
        reason: note.reason,
        tea_purchase_id: isFromPurchase
            ? (firstItem?.tea_purchase_id ?? '')
            : '',
        notes: note.notes ?? '',
        items: isFromPurchase
            ? []
            : (note.items ?? []).map((it) => ({
                  kind: 'existing' as const,
                  purchase_lot_id: it.purchase_lot_id,
                  quantity: it.quantity,
              })),
    }

    // List đơn mua (has_active_pnk:false) loại trừ đơn mua đã có PNK — chính là
    // phiếu này — nên seed đơn mua hiện tại để combobox hiển thị đúng. Ưu tiên
    // nhãn đầy đủ (kèm ngày + kg) từ list không lọc; nếu chưa có thì fallback
    // nhãn ngắn từ item.
    const currentPurchaseOption =
        isFromPurchase && firstItem?.tea_purchase_id
            ? (allTeaPurchaseOptions.find(
                  (o) => o.value === firstItem.tea_purchase_id,
              ) ?? {
                  value: firstItem.tea_purchase_id,
                  label: `${firstItem.purchase_code ?? ''} — ${firstItem.grower_name ?? ''}`,
              })
            : undefined
    const teaPurchaseOptions = currentPurchaseOption
        ? [
              currentPurchaseOption,
              ...freeTeaPurchaseOptions.filter(
                  (o) => o.value !== currentPurchaseOption.value,
              ),
          ]
        : freeTeaPurchaseOptions

    const backToDetail = () =>
        navigate({
            to: '/purchase-lots/stock-in-notes/$id',
            params: { id },
        })

    const handleSubmit = async (value: CreatePurchaseInNoteRequest) => {
        try {
            await update.mutateAsync({
                id,
                req: {
                    reason: value.reason,
                    tea_purchase_id: value.tea_purchase_id,
                    notes: value.notes,
                    items: value.items,
                },
            })
            void backToDetail()
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_new_error_create(),
            )
        }
    }

    return (
        <PurchaseLotStockInForm
            mode="edit"
            title={m.pl_pnk_edit_title()}
            submitLabel={m.pl_pnk_edit_submit()}
            initialValues={initialValues}
            teaPurchaseOptions={teaPurchaseOptions}
            allTeaPurchaseOptions={allTeaPurchaseOptions}
            purchaseLotOptions={lotOptions}
            scannedLots={[...noteLotSeeds, ...Array.from(scannedLots.values())]}
            onLotScanned={(lotId) => void fetchAndCacheLot(lotId)}
            isPending={update.isPending}
            onPurchaseSearchChange={setPurchaseSearch}
            onPurchaseLotSearchChange={setPurchaseLotSearch}
            onCancel={() => void backToDetail()}
            onSubmit={(value) => void handleSubmit(value)}
        />
    )
}
